import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Music } from 'lucide-react';
import api from '../utils/api';

// The party's public stage — thrown on a TV or laptop. Research lesson
// (Jukola, 2004): a shared display is what turns song choice into a
// room-wide social event. Read-only, PIN-gated, no sign-in.
const POLL_MS = 5000;

const TVMode = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const pin = searchParams.get('pin') || '';
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/api/tv/${sessionId}`, { params: { pin } });
        if (alive) {
          setStage(data);
          setError(null);
        }
      } catch (err) {
        if (alive) {
          setError(err.response?.data?.error || 'Could not reach the party');
        }
      }
    };
    load();
    const interval = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [sessionId, pin]);

  const joinUrl = `${window.location.origin}/session/${sessionId}`;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-center p-8">
        <Music className="w-16 h-16 text-gray-600 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Party display</h1>
        <p className="text-gray-400">{error}</p>
        <p className="text-gray-500 text-sm mt-4">
          Open this page from the party's invite screen so the PIN comes along.
        </p>
      </div>
    );
  }

  if (!stage) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
      </div>
    );
  }

  const { nowPlaying, nextUp } = stage;

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-hidden relative flex flex-col">
      {/* Ambient artwork wash */}
      {nowPlaying?.image && (
        <img
          src={nowPlaying.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-125 blur-3xl opacity-25"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950/70 via-gray-950/40 to-gray-950/90" />

      {/* Header */}
      <div className="relative flex items-center justify-between px-10 pt-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{stage.name}</h1>
          <p className="text-white/60 text-sm mt-1">
            {stage.guestCount} {stage.guestCount === 1 ? 'guest' : 'guests'} vibing • powered by JukeHive
          </p>
        </div>
        <div className="flex items-center gap-4 bg-white/10 backdrop-blur rounded-2xl px-5 py-4">
          <div className="bg-white p-2 rounded-xl">
            <QRCodeSVG value={joinUrl} size={96} />
          </div>
          <div>
            <p className="text-sm font-semibold text-white/90">Scan to join the party</p>
            <p className="text-white/60 text-xs mt-1">
              PIN <span className="font-mono text-lg text-white tracking-widest">{stage.joinPin}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Now playing stage */}
      <div className="relative flex-1 flex items-center px-10">
        {nowPlaying ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={nowPlaying.name + nowPlaying.artist}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-10"
            >
              {nowPlaying.image && (
                <img
                  src={nowPlaying.image.replace('100x100', '600x600')}
                  alt={nowPlaying.name}
                  className="w-72 h-72 rounded-3xl object-cover shadow-2xl"
                />
              )}
              <div className="min-w-0">
                <p className="text-white/60 uppercase tracking-[0.3em] text-sm mb-3">Now Playing</p>
                <h2 className="text-6xl font-extrabold leading-tight truncate max-w-3xl">{nowPlaying.name}</h2>
                <p className="text-2xl text-white/80 mt-2 truncate max-w-2xl">{nowPlaying.artist}</p>
                {nowPlaying.addedByName && (
                  <p className="text-xl text-white/70 mt-5">
                    Added by <span className="font-semibold text-white">{nowPlaying.addedByName}</span> 🎉
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <div className="text-center w-full">
            <Music className="w-20 h-20 text-white/30 mx-auto mb-4" />
            <p className="text-2xl text-white/60">Scan the code and start the queue!</p>
          </div>
        )}
      </div>

      {/* Next up rail */}
      {nextUp?.length > 0 && (
        <div className="relative px-10 pb-10">
          <p className="text-white/50 uppercase tracking-[0.25em] text-xs mb-3">Up Next</p>
          <div className="flex gap-4">
            {nextUp.map((song, i) => (
              <div
                key={`${song.name}-${i}`}
                className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-2xl px-4 py-3 flex-1 min-w-0"
              >
                <span className="text-white/40 font-bold text-lg">{i + 1}</span>
                {song.image && (
                  <img src={song.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                )}
                <div className="min-w-0">
                  <p className="font-semibold truncate">{song.name}</p>
                  <p className="text-white/60 text-sm truncate">
                    {song.artist}
                    {song.addedByName ? ` • ${song.addedByName}` : ''}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0 text-sm text-white/70">
                  {song.votes > 0 && <span>▲ {song.votes}</span>}
                  {song.fires > 0 && <span>🔥 {song.fires}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TVMode;
